import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, } from '@/components/ui/carousel';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useController } from '@/hooks/useController';
import { useSocket } from '@/hooks/useSocket';
import { widgetPositionState } from '@/state/config';
import { isWidgetExpandedState, webviewModalState } from '@/state/session';
import { cn } from '../../../lib/utils';
import { WebviewContainer } from '../Webview/WebviewContainer';
import { CarouselCard } from './Card';
export const CustomCarousel = ({ message, disabled, onButtonClick }) => {
    const [api, setApi] = useState();
    const [current, setCurrent] = useState(0);
    const [count, setCount] = useState(0);
    const { sendMessage } = useSocket();
    const { notifyWebviewOpened, notifyWebviewClosed } = useController();
    const [webview, setWebview] = useState(null);
    const setWebviewModal = useSetRecoilState(webviewModalState);
    const [isWebviewExpanded, setIsWebviewExpanded] = useState(false);
    const isWebchatExpanded = useRecoilValue(isWidgetExpandedState);
    const { isPopup } = useRecoilValue(widgetPositionState);
    const shouldExpandCarousel = isWebchatExpanded || isPopup;
    useEffect(() => {
        if (!api) {
            return;
        }
        const updateCarouselState = () => {
            setCount(api.scrollSnapList().length);
            setCurrent(api.selectedScrollSnap() + 1);
        };
        api.on('reInit', updateCarouselState);
        api.on('select', updateCarouselState);
        // Defer initial update to avoid synchronous setState in effect
        queueMicrotask(updateCarouselState);
        return () => {
            api.off('reInit', updateCarouselState);
            api.off('select', updateCarouselState);
        };
    }, [api]);
    const handleDotClick = (index) => {
        api === null || api === void 0 ? void 0 : api.scrollTo(index);
    };
    const hasOneCard = message.cards.length === 1;
    const hasTwoCards = message.cards.length === 2;
    const handlePostback = useCallback((text, label) => {
        sendMessage({ text: text === null || text === void 0 ? void 0 : text.trim(), label });
    }, [sendMessage]);
    const handleWebviewClose = () => {
        setIsWebviewExpanded(false);
        notifyWebviewClosed();
    };
    const handleWebviewClick = useCallback((button) => {
        setWebview(button);
        if (button.height === 'new_window') {
            return;
        }
        if (button.height === 'compact') {
            setWebviewModal(button);
        }
        else {
            notifyWebviewOpened();
            setIsWebviewExpanded(true);
        }
    }, [notifyWebviewOpened, setWebviewModal]);
    return (_jsxs("div", { children: [_jsxs(Carousel, { opts: {
                    align: 'center',
                    containScroll: 'trimSnaps',
                    slidesToScroll: 1,
                    dragFree: true,
                    loop: true,
                    active: !hasOneCard,
                }, setApi: setApi, className: cn('w-[calc(100%+48px)] -translate-x-6', {
                    '-translate-x-12': hasTwoCards && current === 2,
                }), children: [_jsx(CarouselContent, { className: cn('-ml-1', {
                            'ml-0 justify-center': hasOneCard,
                        }), children: message.cards.map((card, index) => (_jsx(CarouselItem, { className: cn('basis-[336px] pl-1', {
                                'basis-[400px]': shouldExpandCarousel,
                                'pl-0': hasOneCard,
                                'ml-5': hasTwoCards && index === 0,
                                'pl-3': shouldExpandCarousel && !hasOneCard,
                            }), children: _jsx(CarouselCard, { card: card, disabled: disabled, onButtonClick: onButtonClick, active: current === index + 1, onPostback: handlePostback, onWebviewClick: handleWebviewClick }) }, index))) }), !hasOneCard && (_jsxs("div", { className: cn('mx-8 mt-4 flex items-center justify-between text-center text-sm text-muted-foreground', {
                            'translate-x-6': hasTwoCards && current === 2,
                            'mx-auto max-w-sm': shouldExpandCarousel,
                        }), children: [_jsx("div", { className: "inline-flex gap-2", children: Array.from({ length: count }).map((_, index) => (_jsx("button", { className: cn('dark:border-stroke h-3 w-3 cursor-pointer rounded-full border-2 border-gray-200 bg-transparent', {
                                        'border-focus-ring dark:border-focus-ring': current === index + 1,
                                    }), onClick: () => handleDotClick(index) }, index))) }), _jsxs("div", { className: "flex gap-4", children: [_jsx(CarouselPrevious, {}), _jsx(CarouselNext, {})] })] }))] }), isWebviewExpanded && webview && (_jsx(Dialog, { open: true, children: _jsx(DialogContent, { onInteractOutside: (e) => e.preventDefault(), onEscapeKeyDown: (e) => e.preventDefault(), className: cn('h-full w-full p-0', {
                        'h-[var(--moveo-modal-tall-height)] w-[var(--moveo-modal-tall-width)]': (webview === null || webview === void 0 ? void 0 : webview.height) === 'tall',
                    }), children: _jsx(WebviewContainer, { webview: webview, onClick: handleWebviewClose }) }) }))] }));
};
